(() => {
  "use strict";

  if (window.__ECOMIFIC_REBUY_REGION_GUARD__) return;
  window.__ECOMIFIC_REBUY_REGION_GUARD__ = true;

  const LOG_NS = "ecomific";
  const MODULE = "rebuy-region-guard";
  const REBUY_WIDGET_SELECTOR = ".rebuy-widget";
  const KNOWN_CARD_SELECTOR = [
    ".rebuy-product-block",
    ".rebuy-product-card",
    ".rebuy-product-grid-item",
    ".rebuy-product"
  ].join(",");
  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';
  const CARD_FLAG_ATTR = "data-ecomific-product-card";
  const HYDRATE_STATE_ATTR = "data-ecomific-rebuy-hydrated";
  const UNKNOWN_ATTR = "data-ecomific-rebuy-unknown";
  const REGION_LS_KEY = "ecomific_region";
  const REGION_COOKIE = "ecomific_region";
  const HIDDEN_CLASS = "ecomific-hidden-by-region";
  const DEV_PREVIEW_KEY = "ecomific_theme_preview";

  const pendingRoots = [];
  const handleCache = new Map();
  let applyTimer = null;

  function isDebugEnabled() {
    if (typeof window === "undefined") return false;
    if (window.ecomificDebug === true || window.__ecomificDebug === true) {
      return true;
    }
    try {
      if (window.localStorage?.getItem?.("ecomific_debug") === "1") {
        return true;
      }
    } catch (err) {}
    try {
      if (window.location?.search) {
        const params = new URLSearchParams(window.location.search);
        if (params.has("ecomificDebug")) return true;
      }
    } catch (err) {}
    return false;
  }

  function fmt(level, msg) {
    return `[${LOG_NS}][${level}] ${MODULE}: ${msg}`;
  }

  function logDebug(msg, ...args) {
    if (!isDebugEnabled()) return;
    console.debug(fmt("debug", msg), ...args);
  }

  function logInfo(msg, ...args) {
    if (!isDebugEnabled()) return;
    console.info(fmt("info", msg), ...args);
  }

  function logWarn(msg, ...args) {
    console.warn(fmt("warn", msg), ...args);
  }

  function normalizeRegion(value) {
    if (typeof value !== "string") return "";
    const normalized = value.trim().toLowerCase();
    return normalized === "east" || normalized === "west" ? normalized : "";
  }

  function normalizeTag(tag) {
    return String(tag || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/_/g, "-");
  }

  function getCookie(name) {
    if (typeof document === "undefined") return "";
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + name + "=([^;]*)")
    );
    return match ? decodeURIComponent(match[1]) : "";
  }

  function getCurrentRegion() {
    try {
      const fromLs = window.localStorage?.getItem?.(REGION_LS_KEY);
      const normalizedLs = normalizeRegion(fromLs);
      if (normalizedLs) return normalizedLs;
    } catch (err) {}
    return normalizeRegion(getCookie(REGION_COOKIE));
  }

  function isThemePreview() {
    try {
      if (window.Shopify?.designMode) return true;
    } catch (err) {}
    try {
      return sessionStorage.getItem(DEV_PREVIEW_KEY) === "1";
    } catch (err) {
      return false;
    }
  }

  function shouldHideCard(card, region) {
    if (!card) return false;
    if (card.hasAttribute("data-ecomific-dev-product") && !isThemePreview()) {
      return true;
    }
    if (!region) return false;
    if (region === "east") return card.hasAttribute("data-ecomific-west-only");
    if (region === "west") return card.hasAttribute("data-ecomific-east-only");
    return false;
  }

  function applyRegionVisibility(card) {
    if (!card) return;
    const regionApi = window.ecomificRegionVisibility;
    if (regionApi && typeof regionApi.apply === "function") {
      regionApi.apply(card);
      return;
    }

    const region = getCurrentRegion();
    if (shouldHideCard(card, region)) {
      card.classList.add(HIDDEN_CLASS);
      card.setAttribute("aria-hidden", "true");
    } else {
      card.classList.remove(HIDDEN_CLASS);
      if (card.getAttribute("aria-hidden") === "true") {
        card.removeAttribute("aria-hidden");
      }
    }
  }

  function markUnknownAndHide(card, reason) {
    if (!card) return;
    card.setAttribute(CARD_FLAG_ATTR, "true");
    card.setAttribute(UNKNOWN_ATTR, reason || "unknown");
    card.setAttribute(HYDRATE_STATE_ATTR, "failed");
    card.classList.add(HIDDEN_CLASS);
    card.setAttribute("aria-hidden", "true");
    logWarn("card hidden (fail-closed)", { reason: reason || "unknown" });
  }

  function parseHandleFromHref(href) {
    if (!href) return "";
    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/^\/products\/([^\/?#]+)/i);
      return match && match[1] ? decodeURIComponent(match[1]) : "";
    } catch (err) {
      return "";
    }
  }

  function getHandleFromClass(card) {
    if (!card || !card.classList) return "";
    for (const cls of card.classList) {
      if (!cls) continue;
      if (cls.indexOf("product-id-") === 0) continue;
      if (cls.indexOf("rebuy") === 0) continue;
      if (cls.indexOf("is-") === 0) continue;
      if (cls.indexOf("cart-") === 0) continue;
      if (/^[a-z0-9][a-z0-9-]*$/.test(cls)) return cls;
    }
    return "";
  }

  function extractHandle(card) {
    if (!card) return "";

    const fromAttrs =
      card.getAttribute("data-product-handle") ||
      card.getAttribute("data-handle") ||
      card.getAttribute("data-product-handle");
    if (fromAttrs) return String(fromAttrs).trim();

    const classHandle = getHandleFromClass(card);
    if (classHandle) return classHandle;

    const link = card.querySelector(PRODUCT_LINK_SELECTOR);
    if (!link || !link.getAttribute) return "";
    return parseHandleFromHref(link.getAttribute("href") || link.href || "");
  }

  function getWidgetRoot(node) {
    if (!node || typeof node.closest !== "function") return null;
    return node.closest(REBUY_WIDGET_SELECTOR);
  }

  function isLikelyCard(node) {
    if (!node || node.nodeType !== 1) return false;
    const el = node;
    if (!getWidgetRoot(el)) return false;
    if (!el.querySelector(PRODUCT_LINK_SELECTOR)) return false;

    // Avoid large wrappers containing many product links.
    const productLinks = el.querySelectorAll(PRODUCT_LINK_SELECTOR).length;
    if (
      productLinks > 3 &&
      !el.matches(".rebuy-product-block, .rebuy-product-card, .rebuy-product-grid-item")
    ) {
      return false;
    }
    return true;
  }

  function findCardFromAnchor(anchor) {
    if (!anchor || anchor.nodeType !== 1) return null;
    const direct = anchor.closest(KNOWN_CARD_SELECTOR);
    if (direct && getWidgetRoot(direct)) return direct;

    let cursor = anchor.parentElement;
    let depth = 0;
    while (cursor && depth < 6) {
      if (!getWidgetRoot(cursor)) break;
      const className = String(cursor.className || "");
      if (
        /\brebuy-product\b/.test(className) ||
        /\brebuy-product-/.test(className)
      ) {
        return cursor;
      }
      cursor = cursor.parentElement;
      depth += 1;
    }

    return null;
  }

  function findCards(root) {
    const scope = root && root.nodeType === 1 ? root : document;
    const found = new Set();

    const knownCards = scope.querySelectorAll?.(KNOWN_CARD_SELECTOR) || [];
    for (const card of knownCards) {
      if (isLikelyCard(card)) found.add(card);
    }

    const links = scope.querySelectorAll?.(
      `${REBUY_WIDGET_SELECTOR} ${PRODUCT_LINK_SELECTOR}`
    ) || [];
    for (const link of links) {
      const card = findCardFromAnchor(link);
      if (card && isLikelyCard(card)) found.add(card);
    }

    return Array.from(found);
  }

  function parseTagFlags(tags) {
    const normalized = new Set(
      (Array.isArray(tags) ? tags : []).map((tag) => normalizeTag(tag))
    );
    return {
      eastOnly: normalized.has("east-only"),
      westOnly: normalized.has("west-only"),
      devProduct: normalized.has("dev-product")
    };
  }

  function setFlag(card, attr, enabled) {
    if (!card) return;
    if (enabled) {
      card.setAttribute(attr, "true");
    } else {
      card.removeAttribute(attr);
    }
  }

  async function fetchProductTagFlags(handle) {
    const normalizedHandle = String(handle || "").trim();
    if (!normalizedHandle) {
      throw new Error("Missing product handle");
    }

    if (handleCache.has(normalizedHandle)) {
      return handleCache.get(normalizedHandle);
    }

    const request = fetch(
      `/products/${encodeURIComponent(normalizedHandle)}.js`,
      { credentials: "same-origin" }
    )
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Product payload failed (${res.status})`);
        }
        const payload = await res.json();
        return parseTagFlags(payload?.tags || []);
      });

    handleCache.set(normalizedHandle, request);
    return request;
  }

  function hasRegionFlags(card) {
    if (!card) return false;
    return (
      card.hasAttribute("data-ecomific-east-only") ||
      card.hasAttribute("data-ecomific-west-only") ||
      card.hasAttribute("data-ecomific-dev-product")
    );
  }

  async function hydrateCard(card) {
    if (!card || card.nodeType !== 1) return;
    if (card.getAttribute(HYDRATE_STATE_ATTR) === "resolved") {
      applyRegionVisibility(card);
      return;
    }

    card.setAttribute(CARD_FLAG_ATTR, "true");

    if (hasRegionFlags(card)) {
      card.setAttribute(HYDRATE_STATE_ATTR, "resolved");
      applyRegionVisibility(card);
      return;
    }

    const handle = extractHandle(card);
    if (!handle) {
      markUnknownAndHide(card, "missing-handle");
      return;
    }

    // Fail-closed while resolving tags so off-region cards never flash.
    card.classList.add(HIDDEN_CLASS);
    card.setAttribute("aria-hidden", "true");

    try {
      const flags = await fetchProductTagFlags(handle);
      setFlag(card, "data-ecomific-east-only", !!flags.eastOnly);
      setFlag(card, "data-ecomific-west-only", !!flags.westOnly);
      setFlag(card, "data-ecomific-dev-product", !!flags.devProduct);
      card.removeAttribute(UNKNOWN_ATTR);
      card.setAttribute(HYDRATE_STATE_ATTR, "resolved");
      applyRegionVisibility(card);
    } catch (error) {
      markUnknownAndHide(card, "tag-fetch-failed");
    }
  }

  function queueHydrate(root) {
    const target = root && root.nodeType === 1 ? root : document;
    if (!pendingRoots.includes(target)) {
      pendingRoots.push(target);
    }

    if (applyTimer) return;
    applyTimer = setTimeout(async () => {
      const roots = pendingRoots.slice();
      pendingRoots.length = 0;
      applyTimer = null;

      let totalCards = 0;
      for (const pendingRoot of roots) {
        const cards = findCards(pendingRoot);
        totalCards += cards.length;
        await Promise.all(cards.map((card) => hydrateCard(card)));
      }

      if (totalCards > 0) {
        logInfo("rebuy cards hydrated", { cardCount: totalCards });
      }
    }, 70);
  }

  function reapplyKnownCards() {
    const cards = document.querySelectorAll(
      `${REBUY_WIDGET_SELECTOR} [${CARD_FLAG_ATTR}]`
    );
    for (const card of cards) {
      applyRegionVisibility(card);
    }
  }

  function shouldInspectNode(node) {
    if (!node || node.nodeType !== 1) return false;
    const el = node;
    if (el.matches?.(REBUY_WIDGET_SELECTOR)) return true;
    if (el.querySelector?.(REBUY_WIDGET_SELECTOR)) return true;
    if (el.matches?.(`${REBUY_WIDGET_SELECTOR} ${PRODUCT_LINK_SELECTOR}`)) {
      return true;
    }
    if (el.querySelector?.(`${REBUY_WIDGET_SELECTOR} ${PRODUCT_LINK_SELECTOR}`)) {
      return true;
    }
    return false;
  }

  function initObserver() {
    if (typeof MutationObserver === "undefined" || !document.body) return;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        const addedNodes = mutation?.addedNodes || [];
        for (const node of addedNodes) {
          if (shouldInspectNode(node)) {
            queueHydrate(node);
            break;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function bindEvents() {
    document.addEventListener("rebuy.ready", () => queueHydrate(document));
    document.addEventListener("rebuy:cart.change", () => queueHydrate(document));
    document.addEventListener("rebuy:smartcart.ready", () => queueHydrate(document));
    document.addEventListener("rebuy:smartcart.show", () => queueHydrate(document));

    window.addEventListener("ecomific:region:ready", () => reapplyKnownCards());
    window.addEventListener("ecomific:region:changed", () => reapplyKnownCards());
  }

  function init() {
    bindEvents();
    initObserver();
    queueHydrate(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
